import { observer } from "mobx-react-lite";
import * as React from "react";
import { classNames } from "src/common/classNames";
import { Empty, Spin } from "antd";
import { useResizeDependency } from "src/common/useResizeDependency";
import { toggleOverlapClass } from "src/common/toggleOverlapClass";
import styles from "./Planning2Gantt.module.less";
import { Planning2GanttStore } from "./Planning2GanttStore";
import { Pl2GanttTools } from "./Pl2GanttTools";
import { Pl2GanttLeftHeadCell } from "./Pl2GanttLeftHeadCell";
import { Pl2GanttView, Pl2GanttViewHeader } from "./Pl2GanttView";

// можно было бы использовать ResizableHorizPart для изменения ширины частей. но тут слишком сложная структура
// поэтому его функционал откопипастщен. Возможно, временно 🤣

// фичи, которые пока отложены:
// - можно оставить только правую вертикальную полосу прокрутки. Но надо реагировать на случай скрытия правой части

type PropsPlanning2Gantt = {
  gantt: Planning2GanttStore;
};

export const Planning2Gantt: React.FC<PropsPlanning2Gantt> = observer(
  (props) => {
    const { gantt } = props;
    const {
      showGantt,
      tableStore,
      leftColumns,
      leftTemplateColumns,
      leftRows,
    } = gantt;
    const rightPartWidth = showGantt ? gantt.rightWidth : "100%";
    const refLeftPart = React.useRef<HTMLDivElement>(null);
    const refLeftHeader = React.useRef<HTMLDivElement>(null);
    const refLeftBodyBox = React.useRef<HTMLDivElement>(null);
    const refRightHead = React.useRef<HTMLDivElement>(null);
    const refRightBody = React.useRef<HTMLDivElement>(null);
    const x0 = React.useRef(0);
    const onDragStart: React.DragEventHandler = (e) => {
      x0.current = e.clientX - (refLeftPart.current?.clientWidth ?? 0);
    };
    const onDrag: React.DragEventHandler = (e) => {
      gantt.changeRightWidth(e.clientX - x0.current);
    };
    const fixedColumns = 2 + tableStore.extraFixedColumns;
    const fixedLefts: number[] = [];
    let offset = 0;
    leftColumns.slice(0, fixedColumns).forEach(({ width }) => {
      fixedLefts.push(offset);
      offset += width;
    });

    const vertScrollSync = (
      scrollTop: number,
      src: "left" | "leftFixed" | "right",
    ) => {
      const left = refLeftBodyBox.current;
      if (src !== "left" && left) {
        left.scrollTop = scrollTop;
      }
      const right = refRightBody.current;
      if (src !== "right" && right) {
        right.scrollTop = scrollTop;
      }
    };

    // если у левого тела появляется вертикальный скроллбар, то ширина заголовка отличается (т.е больше на ширину скроллбара)
    // Поэтому без синхронизации столбцы заголовка расходятся со столбцами тела на ширину скроллбара
    // Теоретически, существует недокументорованное значение overflow, которое может решать данную проблему. Но там вопросы по браузерной совместимости.
    //      Без синхронизации ширины           Ширина заголовка синхронизирована
    // ┌────────────┬───────────┬──────────┐   ┌───────────┬──────────┬─────────┐
    // │   Title 1  │  Title 2  │  Title 3 │   │  Title 1  │  Title 2 │ Title 3 │     Заголовок
    // ├───────────┬┴──────────┬┴────────┬─┤   ├───────────┼──────────┼─────────┼─┐
    // │           │           │         │▲│   │           │          │         │▲│   Тело
    // ├───────────┼───────────┼─────────┤░│   ├───────────┼──────────┼─────────┤░│
    useResizeDependency(refLeftBodyBox, refLeftHeader, (body, header) => {
      // eslint-disable-next-line no-param-reassign
      header.style.width = `${body.clientWidth}px`;
    });
    useResizeDependency(refRightBody, refRightHead, (body, header) => {
      // eslint-disable-next-line no-param-reassign
      header.style.width = `${body.clientWidth}px`;
    });

    // Список видимых задач пуст (без уточнения причины)
    const isEmpty = leftRows.length === 0;
    // Задач нет в исходных данных (т.е. независимо от фильтрации)
    const isEmptyData = gantt.tableStore.allTasks.length === 0;
    const emptyMsg = () =>
      isEmptyData ? "Нет данных" : "Нет данных, отвечающих условиям фильтрации";

    const clsFixed = (colIndex: number): (string | undefined)[] => [
      colIndex < fixedColumns ? styles.fixedCell : undefined,
      colIndex === fixedColumns - 1 ? styles.fixedLastCell : undefined,
    ];

    return (
      <div className={styles.mainBox}>
        {/* Left Part */}
        <div
          ref={refLeftPart}
          className={classNames([
            styles.leftPart,
            [!showGantt, styles.singleLeftPart],
          ])}
          style={{ width: rightPartWidth }}
        >
          <div className={classNames([styles.leftTable])}>
            {isEmpty && (
              <div className={styles.leftEmpty}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={emptyMsg()}
                />
              </div>
            )}
            {/* Заголовочная часть левой таблицы */}
            <div
              ref={refLeftHeader}
              className={styles.leftHead}
              style={{ gridTemplateColumns: leftTemplateColumns }}
            >
              {leftColumns.map((col, colIndex) => (
                <div
                  key={col.key}
                  className={classNames([styles.th, ...clsFixed(colIndex)])}
                  style={{ left: fixedLefts[colIndex] }}
                >
                  <Pl2GanttLeftHeadCell gantt={gantt} column={col} />
                </div>
              ))}
            </div>

            <div
              className={styles.bodyBox}
              ref={refLeftBodyBox}
              onScroll={(e) => {
                const { scrollLeft } = e.currentTarget;
                if (refLeftHeader.current) {
                  // при горизонтальной прокрутке тела необходимо принудительно сдвигать заголовок
                  refLeftHeader.current.scrollLeft = scrollLeft;
                }
                vertScrollSync(e.currentTarget.scrollTop, "left");
                toggleOverlapClass(
                  refLeftPart.current,
                  scrollLeft,
                  styles.fixedLastCell,
                  styles.fixedOverlap,
                );
              }}
            >
              {/* ячейки левой таблицы */}
              <div
                className={styles.leftBody}
                style={{ gridTemplateColumns: leftTemplateColumns }}
              >
                {!isEmpty &&
                  leftRows.map((row) => (
                    <React.Fragment key={row.id}>
                      {leftColumns.map((col, colIndex) => (
                        <div
                          key={col.key}
                          className={classNames([
                            styles.td,
                            ...clsFixed(colIndex),
                          ])}
                          style={{ left: fixedLefts[colIndex] }}
                        >
                          <span>{col.render(col, row)}</span>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                {/* Фиктивный элемент только для того чтобы была правильная ширина и скроллирование */}
                {isEmpty && (
                  <div style={{ gridColumn: `span ${leftColumns.length}` }} />
                )}
              </div>
            </div>
          </div>
          {showGantt && <div className={styles.leftFoot} />}
        </div>

        {showGantt && (
          <>
            <div
              className={styles.splitter}
              draggable
              onDragStart={onDragStart}
              onDrag={onDrag}
              onDragEnd={onDrag}
            >
              <div className={styles.center} />
            </div>

            {/* Right part */}
            <div className={classNames([styles.rightPart])}>
              <div className={classNames([styles.rightTable])}>
                <div ref={refRightHead} className={styles.rightHead}>
                  <Pl2GanttViewHeader gantt={gantt} />
                </div>
                <div
                  ref={refRightBody}
                  className={styles.rightBody}
                  onScroll={({ currentTarget }) => {
                    vertScrollSync(currentTarget.scrollTop, "right");
                    if (refRightHead.current) {
                      refRightHead.current.scrollLeft =
                        currentTarget.scrollLeft;
                    }
                  }}
                >
                  <Pl2GanttView gantt={gantt} />
                </div>
              </div>
              <div className={styles.rightFoot}>
                <Pl2GanttTools gantt={gantt} />
              </div>
            </div>
          </>
        )}
        {tableStore.loading && (
          <div className={styles.loader}>
            <Spin size="large" />
          </div>
        )}
      </div>
    );
  },
);
